import type {
  ApiReturnDeviceSite,
  ApiReturnDeviceType,
  ApiReturnDeviceTypeMeasureType,
  ApiReturnManualSensor,
  ApiReturnMeasurePoint,
  ApiReturnMeasureType,
  ApiReturnProject,
  ApiReturnProjectSite,
  ApiReturnSite,
  ApiReturnThematic,
} from '@highimpact-frontend/api-types';
import UpdateManualDevice from '../../../../components/devices/manualDevice/update/UpdateManualDevice';
import { resetExternalState } from '../../../../store/data/slice';
import { useAppDispatch } from '../../../../store/hooks';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import ListDataGrid from '../../../../components/utils/MUISpecifics/data-grid/ListDataGrig';
import { useCheckPermission } from '../../../../components/utils/permission/helpers/usePermission';
import PermissionDecoratorV2 from '../../../../components/utils/permission/PermissionDecoratorV2';
import { useSubmit } from '../../../../utils/hooks/useSubmit';
import { useTranslation } from 'react-i18next';
import Step1 from '../../../../../src/components/devices/manualDevice/create/CreateManualDevice';
import DeviceService from '../../../../api/services/config/device/DeviceService';
import ConfirmModal from '../../../../components/utils/confirmModal/ConfirmModal';
import { getSitesFromDevice } from '../../../../store/config';
import { DataState } from '../../../../store/data/state';
import { getEntityById, getOrElse } from '../../../../utils/functions';
import { useModal } from '../../../../utils/hooks/useModal';

type RowMUI = Partial<Omit<ApiReturnManualSensor, 'id' | 'type'>> & {
  id: number;
  manualSensor: ApiReturnManualSensor;
  type?: string;
  model?: string;
  site?: string;
  serialNumber?: string;
};

interface ManualSensorsProps {
  manualSensors: ApiReturnManualSensor[];
  thematics: ApiReturnThematic[];
  projects: ApiReturnProject[];
  measure_points: ApiReturnMeasurePoint[];
  project_sites: ApiReturnProjectSite[];
  sites: DataState<string, { [KEY in number]: ApiReturnSite }>;
  device_sites: DataState<string, { [KEY in number]: ApiReturnDeviceSite }>;
  measureTypes: DataState<string, { [KEY in number]: ApiReturnMeasureType }>;
  deviceTypes: ApiReturnDeviceType[];
  deviceTypeMeasureTypes: DataState<
    string,
    { [KEY in number]: ApiReturnDeviceTypeMeasureType }
  >;
}

const ManualSensors: React.FC<ManualSensorsProps> = ({
  manualSensors,
  sites,
  thematics,
  measureTypes,
  measure_points,
  deviceTypes,
  device_sites,
  deviceTypeMeasureTypes,
}) => {
  // Import translations here by applying the hook
  const { t } = useTranslation('components.devices');
  //const { t: t2 } = useTranslation(['collections', 'device_headers']);

  const dispatch = useAppDispatch();
  const { submit } = useSubmit();
  const createModal = useModal();
  const editModal = useModal<{ manualSensor: ApiReturnManualSensor | null }>();

  const deleteModal = useModal<{
    callback: () => void;
    message: string;
    target: string;
    manualSensorId: number;
  }>();

  const { shouldHide: hideEditButton, canDisable: disableEditButton } =
    useCheckPermission('configuration_manual_device_edit_button');
  const { shouldHide: hideDeleteButton, canDisable: disableDeleteButton } =
    useCheckPermission('configuration_manual_device_delete_button');

  const getActiveSite = (manualSensor: ApiReturnManualSensor) => {
    return getSitesFromDevice(manualSensor, device_sites, sites)[0]
      ? getSitesFromDevice(manualSensor, device_sites, sites)[0]
      : null;
  };

  const getDeviceBrands = () => {
    const s = new Set(
      manualSensors.map((md) => md.brand).filter((b): b is string => b != null)
    );
    return [...s];
  };

  const deleteManualSensor = async (id: number) => {
    submit({
      callbacks: [
        async () => DeviceService.delete(id),
        async () => dispatch(resetExternalState({ external_key: 'devices' })),
        async () =>
          dispatch(resetExternalState({ external_key: 'site_devices' })),
        async () =>
          dispatch(
            resetExternalState({ external_key: 'device_measure_types' })
          ),
        async () =>
          dispatch(
            resetExternalState({ external_key: 'device_type_measure_types' })
          ),
      ],
      errorMessages: {
        anyError: t(
          'Une erreur est survenue lors de la suppression du dispositif de mesure'
        ),
        unicityError: 'Vous avez déjà supprimé ce dispositif de mesure',
      },
      successMessage: t('Le dispositif de mesure a été supprimé avec succès'),
    });
  };

  const headingsPrettyNames: Record<string, string> = {
    site: t('Position du site'),
  };

  const createRow = (manualSensor: ApiReturnManualSensor): RowMUI => {
    const deviceType = getEntityById(manualSensor.device_type_id!, deviceTypes);
    return {
      id: manualSensor.id,
      serialNumber: getOrElse(manualSensor.sn),
      type: getOrElse(deviceType?.name),
      model: getOrElse(manualSensor.brand),
      site: getOrElse(getActiveSite(manualSensor)?.name),
      manualSensor,
    };
  };

  const rows: RowMUI[] = manualSensors.map(createRow);

  const createColumn = (field: keyof RowMUI): GridColDef<RowMUI> => ({
    field,
    headerName: headingsPrettyNames[field] ?? field,
    flex: 1,
    minWidth: 100,
    sortable: true,
    filterable: true,
    headerClassName: 'list-data-grid-style_table-header',
  });

  const columns: GridColDef<RowMUI>[] = [
    {
      field: 'type',
      headerName: t('Type'),
      sortable: true,
      minWidth: 100,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
    },

    {
      field: 'model',
      headerName: t('Modèle'),
      sortable: true,
      filterable: true,
      minWidth: 100,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
    },

    {
      field: 'serialNumber',
      headerName: t('Numéro de série'),
      sortable: true,
      disableColumnMenu: true,
      minWidth: 100,
      flex: 1,
      headerClassName: 'list-data-grid-style_table-header',
    },

    ...(Object.keys(headingsPrettyNames) as (keyof RowMUI)[]).map((key) =>
      createColumn(key)
    ),

    {
      field: 'actions',
      type: 'actions',
      headerName: t('Actions'),
      minWidth: 100,
      headerClassName: 'list-data-grid-style_table-header',
      getActions: (
        params: import('@mui/x-data-grid-pro').GridRowParams<RowMUI>
      ) => {
        const row = params.row as RowMUI;
        return [
          !hideEditButton && (
            <GridActionsCellItem
              key={`update-${row.id}-${row.manualSensor.brand}`}
              icon={<EditIcon />}
              label={t('Modifier')}
              data-testid= {`edit-device-${row.manualSensor.name}`}
              onClick={() => {
                editModal.open({ manualSensor: row.manualSensor! });
              }}
              showInMenu
              disabled={disableEditButton}
            />
          ),
          !hideDeleteButton && (
            <GridActionsCellItem
              key={`delete-${row.id}-${row.manualSensor.sn}`}
              icon={<DeleteIcon />}
              label={t('Supprimer')}
              data-testid= {`delete-device-${row.manualSensor.name}`}
              onClick={() => {
                deleteModal.open({
                  callback: () => deleteManualSensor(row.manualSensor.id),
                  message: getActiveSite(row.manualSensor)?.name
                    ? t(
                        'Attention, la suppression de ce capteur va entraîner la suppression de la station sur le site',
                        { siteName: getActiveSite(row.manualSensor)?.name }
                      )
                    : t(
                        'Voulez vous vraiment supprimer ce dispositif de mesure'
                      ),
                  target: '',
                  manualSensorId: row.manualSensor.id,
                });
              }}
              showInMenu={!hideDeleteButton}
              disabled={disableDeleteButton}
            />
          ),
        ].filter(Boolean) as React.ReactElement[];
      },
    },
  ];

  return (
    <div className={`list-container`}>
      <div className="toolbar">
        <PermissionDecoratorV2 item="configuration_manual_device_create_button">
          <button
            type="submit"
            className="uk-button uk-button-default"
            onClick={() => createModal.open()}
          >
            {t("Ajouter un dispositif de mesure à l'inventaire")}
          </button>
        </PermissionDecoratorV2>
      </div>

      <ListDataGrid
        rows={rows}
        columns={columns}
        checkboxSelection={false}
        height="calc(100vh - 80px - 200px)"
        initialSortModel={[
          { field: 'type', sort: 'asc' },
          { field: 'model', sort: 'asc' },
          { field: 'serialNumber', sort: 'asc' },
        ]}
        slotProps={{
          columnMenu: {
            slots: {
              columnMenuSortItem: null,
              columnMenuColumnsItem: null,
              columnMenuPinningItem: null,
            },
          },
        }}
      />

      {deleteModal.isOpen && (
        <ConfirmModal
          onCancel={deleteModal.close}
          onConfirm={() => deleteModal.params?.callback?.()}
          open={deleteModal.isOpen}
        >
          <p>
            {deleteModal.params?.message!}{' '}
            <span className="uk-text-bold">{deleteModal.params?.target}</span> ?
          </p>
        </ConfirmModal>
      )}

      {editModal.isOpen && (
        <UpdateManualDevice
          onCancel={editModal.close}
          isOpen={editModal.isOpen}
          closeModal={editModal.close}
          device_types={deviceTypes}
          deviceTypeMeasureTypes={deviceTypeMeasureTypes}
          measureTypes={measureTypes}
          models={getDeviceBrands()}
          manualSensor={editModal.params!.manualSensor!}
        />
      )}

      {createModal.isOpen && (
        <Step1
          onCancel={createModal.close}
          isOpen={createModal.isOpen}
          closeModal={createModal.close}
          device_types={deviceTypes.filter((dt) => dt.name !== 'Météo France')}
          deviceTypeMeasureTypes={deviceTypeMeasureTypes}
          measureTypes={measureTypes}
          models={getDeviceBrands()}
        />
      )}
    </div>
  );
};

export default ManualSensors;
